/**
 * Phone input helpers for the recipients table. US numbers are the norm
 * (YES! Automotive is in Texas): typing digits renders `(713) 555-0101` and
 * stores `+17135550101`. A number typed with another country code
 * (`+44…`) is kept as E.164 digits and validated on length only.
 */

const E164 = /^\+[1-9]\d{7,14}$/;

/** Digits only; a leading `+` survives so a non-US number is recognisable. */
function rawDigits(input: string): { plus: boolean; digits: string } {
  const trimmed = input.trim();
  return { plus: trimmed.startsWith('+'), digits: trimmed.replace(/\D/g, '') };
}

/** US formatting while typing. Non-US input (`+` and not `+1`) is echoed as `+digits`. */
export function formatPhoneInput(input: string): string {
  const { plus, digits } = rawDigits(input);
  if (plus && digits && !digits.startsWith('1')) return `+${digits.slice(0, 15)}`;
  const d = (digits.startsWith('1') && (plus || digits.length > 10) ? digits.slice(1) : digits).slice(0, 10);
  if (d.length === 0) return plus ? '+' : '';
  if (d.length < 4) return `(${d}`;
  if (d.length < 7) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

/** `+1XXXXXXXXXX` for a complete US number, `+<cc><digits>` for a complete foreign one, else `null`. */
export function toE164(input: string): string | null {
  const { plus, digits } = rawDigits(input);
  if (!digits) return null;
  if (plus && !digits.startsWith('1')) {
    const candidate = `+${digits}`;
    return E164.test(candidate) ? candidate : null;
  }
  const d = digits.startsWith('1') && digits.length === 11 ? digits.slice(1) : digits;
  if (d.length !== 10 || d[0] === '0' || d[0] === '1') return null;
  return `+1${d}`;
}

/** The display form of a stored E.164 value. */
export function formatE164(e164: string | null | undefined): string {
  if (!e164) return '';
  return e164.startsWith('+1') && e164.length === 12 ? formatPhoneInput(e164) : e164;
}
